
import React from 'react';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import GenderField from './fields/GenderField';
import BirthYearField from './fields/BirthYearField';
import MeasurementField from './fields/MeasurementField';
import EthnicityField from './fields/EthnicityField';
import TimeField from './fields/TimeField';
import FormActions from './fields/FormActions';

// Define the form schema
const personalInfoSchema = z.object({
  gender: z.enum(["Woman", "Man", "Transgender", "Non-binary", "Prefer not to respond"], {
    required_error: "Please select a gender identity.",
  }),
  birthYear: z.date({
    required_error: "Please select your birth year.",
  }),
  weight: z.string().min(1, { message: "Please enter your weight." }),
  height: z.string().min(1, { message: "Please enter your height." }),
  weightUnit: z.enum(["kg", "lb"]),
  heightUnit: z.enum(["cm", "in"]),
  ethnicity: z.string().min(1, { message: "Please select your ethnicity." }),
  wakeUpTime: z.string().min(1, { message: "Please select your typical wake-up time." }),
});

interface PersonalInfoEditProps {
  data: z.infer<typeof personalInfoSchema>;
  onSave: (data: z.infer<typeof personalInfoSchema>) => void;
  onCancel: () => void;
}

const PersonalInfoEdit: React.FC<PersonalInfoEditProps> = ({ data, onSave, onCancel }) => {
  const form = useForm<z.infer<typeof personalInfoSchema>>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: data,
  });

  const handleSubmit = (values: z.infer<typeof personalInfoSchema>) => {
    onSave(values);
  };

  const weightUnits = [
    { value: "kg", label: "kg" },
    { value: "lb", label: "lb" }
  ];

  const heightUnits = [
    { value: "cm", label: "cm" },
    { value: "in", label: "in" }
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <GenderField form={form} />
        <BirthYearField form={form} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MeasurementField 
            form={form} 
            valueName="weight" 
            unitName="weightUnit" 
            label="Weight" 
            units={weightUnits} 
          />

          <MeasurementField 
            form={form} 
            valueName="height" 
            unitName="heightUnit" 
            label="Height" 
            units={heightUnits} 
          />
        </div>

        <EthnicityField form={form} />
        <TimeField 
          form={form} 
          name="wakeUpTime" 
          label="What time do you typically wake up?" 
        />

        <FormActions onCancel={onCancel} />
      </form>
    </Form>
  );
};

export default PersonalInfoEdit;
